import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import OptimizedImage from '../components/OptimizedImage';
import { getActiveSeason, getSeasonState, getNextRegistration } from '../config/seasons';

// Evergreen. Nothing here should name a date, a venue, or a price — those
// belong to a season, and a season belongs on its own page (/2026, and /2027
// when it exists). Wanting to write "this year…" here is the signal that it
// goes somewhere else.

const BELIEFS = [
  {
    title: 'Every dancer deserves a stage',
    body: `Ballet competitions are usually built for one kind of dancer: young, pre-professional,
           on a track. Ours is open to anyone who trains — a seven-year-old in their first
           variation, a teenager auditioning next season, an adult who came back to ballet at
           forty. Same stage, same lighting, same panel.`,
  },
  {
    title: 'Feedback worth keeping',
    body: `Every competitor leaves with a written report from the panel, not just a score: what
           worked, and what to take back into the studio. A placement lasts a day. Knowing exactly
           what to work on lasts a season.`,
  },
  {
    title: 'Your performance video, always free',
    body: `Every dancer is professionally filmed, and the video is yours at no extra cost — not an
           add-on, not a package. We invest in your growth and your journey, and this is our gift
           to you.`,
  },
];

const DIVISIONS = [
  ['Ages 7–9', 'Soft shoes'],
  ['Ages 10–11', 'Soft shoes'],
  ['Ages 12–14', 'Pointe'],
  ['Ages 15–17', 'Pointe'],
  ['Adult Competitive', 'For advanced adult dancers'],
  ['Adult Recreational', 'For adults who train for the love of it. Pointe and soft shoes are separate divisions.'],
];

const SEASON_STAGES = [
  ['Early Bird', 'Registration opens at a reduced rate for a limited window. Subscribers hear first.'],
  ['Regular Registration', 'Choose your division and variation, and upload your music.'],
  ['Registration Closes', 'Roughly two weeks before competition day. Music and variation are final; the running order follows shortly after.'],
  ['Competition Day', 'Two sessions, each closing with its own awards ceremony.'],
];

const Competition = () => {
  const season = getActiveSeason();
  const state = getSeasonState(season.year);
  // Open or about to open: point at this season. Otherwise at the next one.
  const upcoming = state === 'open' || state === 'coming_soon';
  const next = getNextRegistration();

  return (
    <Layout
      title="Our Philosophy | Prix du Canada Ballet, Toronto"
      description="What Prix du Canada is, who it is for, and how it is judged. An inclusive classical ballet competition in Toronto for dancers aged seven through adult."
      keywords="ballet competition Toronto, adult ballet competition, recreational ballet competition, inclusive ballet competition Canada, classical ballet competition Ontario"
    >
      <section className="elegant-hero" id="overview">
        <div className="hero-background">
          <img
            src="/photos/adult-rec-first-place-dancer-high-alasecond-1920.jpg"
            srcSet="/photos/adult-rec-first-place-dancer-high-alasecond-640.jpg 640w, /photos/adult-rec-first-place-dancer-high-alasecond-1280.jpg 1280w, /photos/adult-rec-first-place-dancer-high-alasecond-1920.jpg 1920w"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            style={{ opacity: 0.18, objectFit: 'cover', width: '100%', height: '100%' }}
          />
        </div>
        <div className="container">
          <h1>Our Philosophy</h1>
          <p style={{ textAlign: 'center' }}>
            A classical ballet competition in Toronto, open to dancers from age seven to adult.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section" style={{ paddingTop: '3rem' }}>
        <div className="container" style={{ maxWidth: '54rem' }}>
          <h2 style={{ textAlign: 'center' }}>Why We Started</h2>
          <div style={{ fontSize: '1.08rem', lineHeight: 1.85, color: 'var(--charcoal)' }}>
            <p>
              Prix du Canada was founded on a straightforward conviction: the stage belongs to every
              dancer who trains for it. Classical ballet asks the same discipline of a young dancer
              preparing a first variation as it does of an adult who returns to the barre after
              work, and the commitment behind each deserves the same professional setting.
            </p>
            <p>
              So we built a competition for every kind of dancer. Young artists working
              toward a career, students who dance for the love of the art, and adults who never
              stopped are all taken equally seriously — each adjudicated against the standards of
              their own division, and each leaving with a written report from the panel.
            </p>
            <p>
              How the day feels matters as much as how it is judged. Ballet can be an unforgiving
              world, and a competition should not add to that. We ask everyone in the building —
              dancers, teachers and families — to hold to a supportive standard, and our feedback
              is written to help a dancer grow rather than to measure them against the room.
            </p>
            <p style={{ marginBottom: 0 }}>
              Our inaugural season brought together dancers aged seven to adult across six divisions.{' '}
              <Link to="/2026" style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
                See how it went →
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: '0 0 1rem' }}>
        <div className="container" style={{ maxWidth: '740px' }}>
          <OptimizedImage
            slug="junior-dancer-first-place-award-announcement"
            alt="A first place announcement at the Prix du Canada award ceremony"
            sizes="(max-width: 780px) 100vw, 740px"
            rounded
          />
        </div>
      </section>

      {/* Beliefs */}
      <section className="section" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>What We Believe</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '2.5rem auto 0',
          }}>
            {BELIEFS.map((b) => (
              <div key={b.title} className="card"
                   style={{ padding: '1.75rem 1.5rem', borderTop: '4px solid var(--primary-gold)', textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.1rem', margin: '0 0 0.75rem', color: 'var(--charcoal)' }}>
                  {b.title}
                </h3>
                <p style={{ margin: 0, fontSize: '0.97rem', lineHeight: 1.7, color: 'var(--soft-gray)' }}>
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divisions & categories */}
      <section className="section" id="divisions">
        <div className="container" style={{ maxWidth: '52rem' }}>
          <h2 style={{ textAlign: 'center' }}>Divisions &amp; Categories</h2>
          <p style={{
            textAlign: 'center', fontSize: '1.08rem', color: 'var(--soft-gray)',
            maxWidth: '46rem', margin: '0 auto 2.5rem', lineHeight: 1.7,
          }}>
            Dancers compete within their age division. Ages are determined as of January 1 of the
            competition year. All divisions welcome dancers of every gender identity.
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: '1rem', marginBottom: '2.5rem',
          }}>
            {DIVISIONS.map(([name, note]) => (
              <div key={name} className="card"
                   style={{ padding: '1.1rem 1.25rem', borderLeft: '4px solid var(--primary-gold)', textAlign: 'left' }}>
                <div style={{ fontWeight: 700, color: 'var(--charcoal)', fontSize: '1rem' }}>{name}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--soft-gray)', marginTop: '0.25rem' }}>{note}</div>
              </div>
            ))}
          </div>

          {upcoming && (
            <div style={{
              padding: '1.5rem', background: 'var(--soft-cream)',
              borderLeft: '4px solid var(--deep-gold)', borderRadius: '8px', textAlign: 'left',
            }}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: 'var(--charcoal)' }}>
                New for {season.year}
              </h3>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--charcoal)', lineHeight: 1.8, fontSize: '0.98rem' }}>
                <li><strong>Ensembles</strong>: dance in a group of 3 to 8, as well as solo.</li>
                <li><strong>Adult Recreational split by shoes</strong>: pointe and soft shoes are judged in separate divisions.</li>
              </ul>
              <p style={{ margin: '1rem 0 0', fontSize: '0.95rem' }}>
                <Link to="/competition-rules" style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>
                  See the {season.year} Rules &amp; Fees →
                </Link>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* How a season runs */}
      <section className="section" style={{ background: 'var(--light-gray)' }} id="timeline">
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>How a Season Runs</h2>
          <p style={{
            textAlign: 'center', fontSize: '1.08rem', color: 'var(--soft-gray)',
            maxWidth: '46rem', margin: '0 auto 2.5rem', lineHeight: 1.7,
          }}>
            Every season follows the same four stages. Exact dates, venue and pricing are confirmed
            when registration opens.
          </p>

          <div style={{
            maxWidth: '1000px', margin: '0 auto', display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem',
          }}>
            {SEASON_STAGES.map(([title, body], i) => (
              <div key={title} className="card"
                   style={{ padding: '1.5rem 1.25rem', borderTop: '4px solid var(--primary-gold)', textAlign: 'left' }}>
                <div style={{
                  fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'var(--primary-gold)', fontWeight: 700, marginBottom: '0.5rem',
                }}>Stage {i + 1}</div>
                <h3 style={{ fontSize: '1.02rem', margin: '0 0 0.6rem', color: 'var(--charcoal)' }}>{title}</h3>
                <p style={{ margin: 0, fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--soft-gray)' }}>{body}</p>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', marginTop: '2.5rem', fontSize: '1rem', color: 'var(--soft-gray)' }}>
            {state === 'open'
              ? <>Registration for {season.year} is open.</>
              : <>Registration for {upcoming ? season.year : next.year} opens <strong>{next.opensPhrase}</strong>.</>}{' '}
            <Link to={upcoming ? '/register' : '/#newsletter'} style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
              {upcoming ? `Register for ${season.year} →` : 'Get notified →'}
            </Link>
          </p>
        </div>
      </section>

      {/* Judging & awards */}
      <section className="section" id="judging">
        <div className="container" style={{ maxWidth: '54rem' }}>
          <h2 style={{ textAlign: 'center' }}>Judging &amp; Awards</h2>
          <div style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--charcoal)' }}>
            <p>
              A panel of three adjudicates every variation, scoring <strong>ballet foundation</strong>,{' '}
              <strong>technique application</strong>, and <strong>artistic presentation</strong> — the
              last weighted double. Dancers are ranked only within their own division.
            </p>
            <p>
              Every competitor receives a certificate of participation and a written report from the
              panel. Medals go to the leading placements in each division, and the number awarded
              scales with how many dancers competed in it: a division of thirty recognises more
              placements than a division of three.
            </p>
            <p style={{ marginBottom: 0 }}>
              Reports are released after the competition and appear in each dancer's profile.
            </p>
          </div>
        </div>
      </section>

      {/* On the day — the evergreen half of the old competitor notes */}
      <section className="section" style={{ background: 'var(--light-gray)' }} id="competitor-notes">
        <div className="container" style={{ maxWidth: '54rem' }}>
          <h2 style={{ textAlign: 'center' }}>On the Day</h2>
          <p style={{
            textAlign: 'center', color: 'var(--soft-gray)', fontSize: '1.02rem',
            lineHeight: 1.7, margin: '0 auto 2rem',
          }}>
            Competitors check in at the registration desk before their session and receive a
            backstage pass. Dressing rooms and a warm-up space are available, and there is a brief
            spacing rehearsal on stage before doors open. Exact times reach every competitor by
            email with the running order.
          </p>
          <p style={{ textAlign: 'center', margin: 0 }}>
            <Link to="/competition-rules" style={{ color: 'var(--primary-gold)', fontWeight: 600, fontSize: '1.05rem' }}>
              Full rules &amp; requirements →
            </Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Competition;
